import {Injectable} from '@angular/core';
import {computeVatReturn} from '@vat/engine/compute-vat-return';
import type {VatEngineInput, VatEngineResult} from '@vat/engine/types';

export interface VatComputeEntry {
    returnKey: string;
    input: VatEngineInput;
    result: VatEngineResult;
    computedAt: Date;
}

/**
 * Session-Cache der Engine (Spec 5 „Caching“): je returnKey Input, Ergebnis und inputHash.
 * Inline-Korrekturen patchen den Input ohne Reload (`patchInput`), „Neu berechnen“ ruft
 * `invalidate` + neuen Ladevorgang. Component-provided – ein Routenwechsel zerstört die
 * Instanz und damit den Cache.
 *
 * @author Anthony Nahas
 * @since 11.09.26
 */
@Injectable()
export class VatComputeService {

    private readonly cache = new Map<string, VatComputeEntry>();

    has(returnKey: string): boolean {
        return this.cache.has(returnKey);
    }

    get(returnKey: string): VatComputeEntry | null {
        return this.cache.get(returnKey) ?? null;
    }

    compute(returnKey: string, input: VatEngineInput): VatComputeEntry {
        const entry: VatComputeEntry = {returnKey, input, result: computeVatReturn(input), computedAt: new Date()};
        this.cache.set(returnKey, entry);
        return entry;
    }

    recompute(returnKey: string): VatComputeEntry | null {
        const existing = this.cache.get(returnKey);
        return existing ? this.compute(returnKey, existing.input) : null;
    }

    patchInput(returnKey: string, patch: (input: VatEngineInput) => VatEngineInput): VatComputeEntry | null {
        const existing = this.cache.get(returnKey);
        return existing ? this.compute(returnKey, patch(existing.input)) : null;
    }

    invalidate(returnKey?: string): void {
        if (returnKey === undefined) {
            this.cache.clear();
        } else {
            this.cache.delete(returnKey);
        }
    }
}
